// src/utils/notificationSound.ts - Service de son pour les notifications

/**
 * Service pour jouer des sons de notification
 */
class NotificationSoundService {
  private audio: HTMLAudioElement | null = null;
  private hasUserInteracted = false;
  private repeatInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Détecter la première interaction utilisateur
    if (typeof window !== 'undefined') {
      const markInteraction = () => {
        this.hasUserInteracted = true;
        // Retirer les listeners après la première interaction
        document.removeEventListener('click', markInteraction);
        document.removeEventListener('keydown', markInteraction);
      };
      document.addEventListener('click', markInteraction);
      document.addEventListener('keydown', markInteraction);
    }
  }

  /**
   * Joue un son de notification
   */
  playNotificationSound() {
    if (!this.hasUserInteracted) {
      console.log('⚠️ [NotificationSound] Utilisateur n\'a pas encore interagi, son non joué');
      return;
    }

    try {
      // Créer ou réutiliser l'audio
      if (!this.audio) {
        this.audio = new Audio('/sounds/notification.mp3');
        this.audio.volume = 0.5; // Volume à 50%
      }

      // Jouer le son
      this.audio.currentTime = 0; // Reset au début
      this.audio.play().catch(error => {
        console.warn('⚠️ [NotificationSound] Fichier MP3 introuvable, utilisation du son de fallback');
        // Fallback : générer un son simple avec Web Audio API
        this.playBeep();
      });

      console.log('🔊 [NotificationSound] Son joué');
    } catch (error) {
      console.error('❌ [NotificationSound] Erreur:', error);
      this.playBeep();
    }
  }

  /**
   * Joue un beep simple (fallback si notification.mp3 n'existe pas)
   */
  private playBeep() {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Premier "ding" à 800 Hz
      const oscillator1 = audioContext.createOscillator();
      const gainNode1 = audioContext.createGain();
      
      oscillator1.connect(gainNode1);
      gainNode1.connect(audioContext.destination);
      
      oscillator1.frequency.value = 800;
      oscillator1.type = 'sine';
      
      gainNode1.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator1.start(audioContext.currentTime);
      oscillator1.stop(audioContext.currentTime + 0.3);
      
      // Deuxième "ding" à 1000 Hz (plus aigu)
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);
      
      oscillator2.frequency.value = 1000;
      oscillator2.type = 'sine';
      
      gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime + 0.15);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
      
      oscillator2.start(audioContext.currentTime + 0.15);
      oscillator2.stop(audioContext.currentTime + 0.4);
      
      console.log('🔊 [NotificationSound] Beep de fallback joué');
    } catch (error) {
      console.error('❌ [NotificationSound] Erreur beep:', error);
    }
  }

  /**
   * Change le volume (0 à 1)
   */
  setVolume(volume: number) {
    if (this.audio) {
      this.audio.volume = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Démarre la répétition du son toutes les X minutes
   * @param intervalMinutes - Intervalle en minutes entre chaque répétition
   */
  startRepeating(intervalMinutes: number = 3) {
    // Arrêter toute répétition en cours
    this.stopRepeating();

    console.log(`🔁 [NotificationSound] Démarrage répétition toutes les ${intervalMinutes} min`);

    // Créer un nouvel intervalle
    this.repeatInterval = setInterval(() => {
      console.log('🔁 [NotificationSound] Répétition du son...');
      this.playNotificationSound();
    }, intervalMinutes * 60 * 1000); // Convertir minutes en millisecondes
  }

  /**
   * Arrête la répétition du son
   */
  stopRepeating() {
    if (this.repeatInterval) {
      clearInterval(this.repeatInterval);
      this.repeatInterval = null;
      console.log('⏹️ [NotificationSound] Arrêt de la répétition');
    }
  }

  /**
   * Vérifie si la répétition est active
   */
  isRepeating(): boolean {
    return this.repeatInterval !== null;
  }
}

// Instance singleton
export const notificationSound = new NotificationSoundService();

/**
 * Fonction utilitaire pour jouer le son de notification
 */
export const playNotificationSound = () => {
  notificationSound.playNotificationSound();
};

/**
 * Démarre la répétition du son
 */
export const startRepeatingSound = (intervalMinutes: number = 3) => {
  notificationSound.startRepeating(intervalMinutes);
};

/**
 * Arrête la répétition du son
 */
export const stopRepeatingSound = () => {
  notificationSound.stopRepeating();
};

export default notificationSound;
